import React from 'react';

const Toggle = ({ label, desc, checked, onChange }) => (
  <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
    <div>
      <span className="text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>{label}</span>
      {desc && <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>{desc}</p>}
    </div>
    <button
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0"
      style={{ background: checked ? 'var(--color-accent)' : 'var(--color-muted-foreground, #6b7280)', opacity: checked ? 1 : 0.4 }}
      role="switch"
      aria-checked={checked}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

export default function NotificationsTab({ prefs, onChange, user }) {
  const set = (key) => (val) => onChange({ ...prefs, [key]: val });

  const alertTypes = prefs?.alertTypes || {
    exposureWarning: true,
    exposureCritical: true,
    inventoryAge: true,
    marginBelow: true,
    benchmarkChange: false,
  };
  const setAlertType = (key) => (val) => onChange({ ...prefs, alertTypes: { ...alertTypes, [key]: val } });

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border p-6" style={{ background: 'var(--color-card)' }}>
        <h3 className="text-foreground font-semibold mb-4" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Notificaciones por email</h3>
        <Toggle
          label="Activar notificaciones por email"
          desc="Recibe alertas importantes en tu correo"
          checked={prefs?.emailNotifications !== false}
          onChange={set('emailNotifications')}
        />
        <div className="py-3 border-b border-border">
          <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Email para alertas</label>
          <input
            type="email"
            value={prefs?.alertEmail || user?.email || ''}
            onChange={(e) => set('alertEmail')(e?.target?.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 transition-colors"
            style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}
            placeholder={user?.email || 'tu@email.com'}
          />
          <p className="text-xs text-muted-foreground mt-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Por defecto usa el email de la cuenta</p>
        </div>
        <div className="py-3">
          <label className="block text-sm font-medium text-foreground mb-2" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Frecuencia de notificaciones</label>
          <div className="flex gap-2 flex-wrap">
            {[{ value: 'immediate', label: 'Inmediata' }, { value: 'daily', label: 'Resumen diario' }, { value: 'weekly', label: 'Resumen semanal' }]?.map((opt) => (
              <button
                key={opt?.value}
                onClick={() => set('notifFrequency')(opt?.value)}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  (prefs?.notifFrequency || 'immediate') === opt?.value
                    ? 'text-white border-transparent' :'text-foreground border-border hover:bg-muted'
                }`}
                style={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  background: (prefs?.notifFrequency || 'immediate') === opt?.value ? 'var(--color-accent)' : undefined,
                }}
              >
                {opt?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-border p-6" style={{ background: 'var(--color-card)' }}>
        <h3 className="text-foreground font-semibold mb-4" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Tipos de alerta</h3>
        {[
          { key: 'exposureWarning', label: 'Exposición supera umbral de advertencia' },
          { key: 'exposureCritical', label: 'Exposición supera umbral crítico' },
          { key: 'inventoryAge', label: 'Alerta de antigüedad de inventario' },
          { key: 'marginBelow', label: 'Margen por debajo del mínimo' },
        ]?.map(({ key, label }) => (
          <Toggle key={key} label={label} desc="" checked={alertTypes?.[key] !== false} onChange={setAlertType(key)} />
        ))}
        <div className="py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>Cambio de benchmark &gt; X%</span>
            <button
              onClick={() => setAlertType('benchmarkChange')(!alertTypes?.benchmarkChange)}
              className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0"
              style={{ background: alertTypes?.benchmarkChange ? 'var(--color-accent)' : undefined, opacity: alertTypes?.benchmarkChange ? 1 : 0.4 }}
              role="switch"
              aria-checked={alertTypes?.benchmarkChange}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${alertTypes?.benchmarkChange ? 'translate-x-4' : 'translate-x-1'}`} />
            </button>
          </div>
          {alertTypes?.benchmarkChange && (
            <div className="flex items-center gap-2 mt-2">
              <input
                type="number"
                min="0.5"
                max="20"
                step="0.5"
                value={prefs?.benchmarkChangePct || 3}
                onChange={(e) => set('benchmarkChangePct')(parseFloat(e?.target?.value))}
                className="w-24 px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2"
                style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem' }}
              />
              <span className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>% de variación para activar alerta</span>
            </div>
          )}
        </div>
      </div>
      <div className="rounded-xl border border-border p-6" style={{ background: 'var(--color-card)' }}>
        <h3 className="text-foreground font-semibold mb-4" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Alertas en la aplicación</h3>
        <Toggle
          label="Sonido de alertas in-app"
          desc="Reproducir sonido al recibir nuevas alertas"
          checked={prefs?.alertSound !== false}
          onChange={set('alertSound')}
        />
      </div>
    </div>
  );
}
